import { TripReason } from './assignUnitToTrip';

import { container } from '@/container';
import CONSTANTS from '@/helpers/constants';
import { Logger } from '@/services/Logger';
import { TripUpdateReq } from '@/services/signalr/MessageTypes/TripUpdateReq';
import { Signalr } from '@/services/signalr/Signalr';
import { recordStatusChangeSent } from '@/utils/statusChangeTimings';

export interface ChangeUnitStatusParams {
  trip: {
    id: string;
  };
  toStatusId?: string | number;
  fromStatusId?: string | number;
  toStatusLabel?: string;
  specialTimestampId?: string | number;
  reason?: TripReason[];
  /** FE-only flag: indicates the transition is from Pre-Assigned state (for toast display) */
  fromPreAssigned?: boolean;
}

/**
 * Change unit status for a trip via SignalR.
 * Handles both regular status changes and special timestamp updates.
 * @param params - The status change parameters
 */
export async function changeUnitStatus(params: ChangeUnitStatusParams): Promise<void> {
  const startMs = performance.now();
  try {
    const signalr = await container.getAsync(Signalr);

    if (params.toStatusId !== undefined && params.toStatusId !== params.fromStatusId) {
      //  Record timing for round-trip lag measurement (consumed by TripManager.onTripUpdateMsg)
      recordStatusChangeSent(params.trip.id, `${params.toStatusId}`, params.toStatusLabel);

      await signalr.invoke(TripUpdateReq, {
        contextKey: CONSTANTS.UI.CONTEXTS.STATUS_CHANGER,
        id: params.trip.id,
        status: {
          id: `${params.toStatusId}`,
          value: params.toStatusLabel,
        },
        ...(params.fromStatusId !== undefined && { fromStatus: { id: `${params.fromStatusId}` } }),
        ...(params.fromPreAssigned && { fromPreAssigned: true }),
        tripReasons: params.reason,
      }, { emitRequest: false });
    }

    if (params.specialTimestampId) {
      await signalr.invoke(TripUpdateReq, {
        contextKey: CONSTANTS.UI.CONTEXTS.SPECIAL_TIMESTAMP,
        id: params.trip.id,
        status: {
          id: `${params.specialTimestampId}`,
        },
        tripReasons: params.reason,
      });
    }

    const logger = container.get(Logger);
    logger.track('trip.stamp', {
      durationMs: Math.round(performance.now() - startMs),
      ...(params.fromStatusId !== undefined && { fromStatus: `${params.fromStatusId}` }),
      ...(params.toStatusId !== undefined && { toStatus: `${params.toStatusId}` }),
      ...(params.toStatusLabel !== undefined && { toStatusLabel: params.toStatusLabel }),
      isSpecialTimestamp: !!params.specialTimestampId,
      success: true,
    });
  }
  catch (error) {
    const logger = container.get(Logger);
    logger.track('trip.stamp', {
      durationMs: Math.round(performance.now() - startMs),
      ...(params.fromStatusId !== undefined && { fromStatus: `${params.fromStatusId}` }),
      ...(params.toStatusId !== undefined && { toStatus: `${params.toStatusId}` }),
      ...(params.toStatusLabel !== undefined && { toStatusLabel: params.toStatusLabel }),
      isSpecialTimestamp: !!params.specialTimestampId,
      success: false,
      errorType: (error as Error).name,
    });
    logger.captureException(error as Error, { tags: { updateTrip: 'changeUnitStatus' } });
    throw error;
  }
}
